import Originals from './OriginalBoards.mjs';
import { Game } from './Game';
import { normalizeBoard, MAX_BOARDS } from './BoardPack.mjs';
import { ORIGINAL_SET, MAX_SETS, makeId, originalId, isOriginal, resolveBoard, resolveEntry, resolveSet, exportLibrary, saveRevision } from './Library.mjs';
import { RevisionUI } from './RevisionUI';
import { TestUI } from './TestUI';

const $ = id => document.getElementById(id);
const button = (text, action, label = text) => {
    const element = document.createElement('button');
    element.textContent = text; element.setAttribute('aria-label', label); element.onclick = action;
    return element;
};
const originBadge = original => {
    const badge = document.createElement('span');
    badge.className = `origin-badge ${original ? 'original' : 'custom'}`;
    badge.textContent = original ? 'Original' : 'Custom';
    return badge;
};

export const LibraryUI = {
    init(workshop) {
        this.workshop = workshop;
        workshop.browser = this;
        this.previewId = workshop.current().id;
        $('library-tab').onclick = $('browse-boards').onclick = () => workshop.show('library');
        $('campaign').onchange = () => { workshop.library.campaignId = $('campaign').value; workshop.persist(); this.renderCampaign(); };
        $('start-game').onclick = () => this.playSelected();
        Game.launchSelected = () => this.playSelected();
        $('load-game').onclick = () => { workshop.pendingImportForPlay = true; $('import-file').click(); };
        $('library-import').onclick = () => { workshop.pendingImportForPlay = false; $('import-file').click(); };
        $('library-backup').onclick = () => workshop.attempt(() => workshop.openJSON(false, exportLibrary(workshop.library)));
        $('board-search').oninput = () => this.renderCards();
        $('board-filter').onchange = () => this.renderCards();
        $('library-undo').onclick = () => $('undo').click();
        $('preview-test').onclick = () => workshop.attempt(() => {
            const original = isOriginal(this.previewId);
            if (!original) {
                workshop.selected = workshop.boards.findIndex(board => board.id === this.previewId);
                workshop.changed();
            }
            TestUI.start(this.preview(), original
                ? { returnView: 'library', returnLabel: 'Return to Library', returnFocus: 'preview-test', context: 'Original' }
                : {});
        });
        $('preview-edit').onclick = () => {
            if (isOriginal(this.previewId)) {
                workshop.add([{ ...this.preview(), name: (this.preview().name + ' — remix').slice(0, 60) }]);
                this.previewId = workshop.current().id;
            } else workshop.selected = workshop.boards.findIndex(board => board.id === this.previewId);
            workshop.changed(); workshop.show(true);
        };
        $('preview-add').onclick = () => workshop.attempt(() => {
            const set = this.currentSet();
            if (set.id === ORIGINAL_SET) throw new Error('Create or select a custom level set first. The original campaign is read-only.');
            if (set.boardIds.length >= MAX_BOARDS) throw new Error(`A set can hold at most ${MAX_BOARDS} levels.`);
            if (!isOriginal(this.previewId)) normalizeBoard(this.preview());
            workshop.remember();
            const pin = isOriginal(this.previewId) ? null : saveRevision(workshop.library, this.previewId).id;
            set.boardIds.push(this.previewId); set.revisionIds.push(pin); workshop.changed();
        });
        $('set-select').onchange = () => { workshop.library.selectedSetId = $('set-select').value; workshop.persist(); this.render(); };
        $('new-set').onclick = () => workshop.attempt(() => {
            if (workshop.library.sets.length >= MAX_SETS) throw new Error(`The library can hold at most ${MAX_SETS} custom level sets.`);
            workshop.remember();
            const set = { id: makeId('set'), name: `My Series ${workshop.library.sets.length + 1}`, boardIds: [], revisionIds: [] };
            workshop.library.sets.push(set); workshop.library.selectedSetId = set.id; workshop.changed();
            $('set-name').focus(); $('set-name').select();
        });
        $('set-name').onfocus = () => { if (this.currentSet().id !== ORIGINAL_SET) workshop.remember(); };
        $('set-name').oninput = () => {
            const set = this.currentSet();
            if (set.id === ORIGINAL_SET) return;
            const name = $('set-name').value.trim();
            if (!name || name.length > 60) return;
            set.name = name; workshop.persist(); this.renderCampaign();
            $('set-select').selectedOptions[0].textContent = `Custom Series · ${name}`;
        };
        $('set-name').onblur = () => { $('set-name').value = this.currentSet().name; this.renderPreview(); };
        $('delete-set').onclick = () => {
            const set = this.currentSet();
            if (set.id === ORIGINAL_SET) return;
            workshop.remember();
            workshop.library.sets = workshop.library.sets.filter(item => item.id !== set.id);
            workshop.library.selectedSetId = ORIGINAL_SET;
            if (workshop.library.campaignId === set.id) workshop.library.campaignId = ORIGINAL_SET;
            workshop.changed(); workshop.notify('Level set removed. Its boards are still in the library. Undo restores the set.');
        };
        $('export-set').onclick = () => workshop.attempt(() => workshop.openJSON(false, exportLibrary(workshop.library, this.currentSet().id)));
        $('choose-set').onclick = () => {
            workshop.library.campaignId = this.currentSet().id;
            workshop.persist(); workshop.show(false);
        };
    },

    currentSet() {
        return this.workshop.library.sets.find(set => set.id === this.workshop.library.selectedSetId) ||
            { id: ORIGINAL_SET, name: 'Original campaign', boardIds: Originals.map((_, index) => originalId(index)) };
    },
    preview() { return resolveBoard(this.workshop.library, this.previewId, Originals); },
    allBoards() { return [...this.workshop.boards, ...Originals.map((board, index) => ({ ...board, id: originalId(index) }))]; },
    playable(board) { try { normalizeBoard(board); return true; } catch { return false; } },

    render() {
        if (!this.preview()) this.previewId = this.workshop.current()?.id || originalId(0);
        this.renderCampaign(); this.renderCards(); this.renderPreview(); this.renderSets();
        $('library-undo').disabled = !this.workshop.history.length;
        $('library-storage').textContent = $('storage-status').textContent;
    },

    renderCampaign() {
        const library = this.workshop.library;
        const options = [new Option('Original campaign - 7 boards', ORIGINAL_SET), ...library.sets.map(set => new Option(`Custom Series · ${set.name} · ${set.boardIds.length} level${set.boardIds.length === 1 ? '' : 's'}`, set.id))];
        $('campaign').replaceChildren(...options);
        $('campaign').value = library.campaignId;
        if (!$('campaign').value) { library.campaignId = ORIGINAL_SET; $('campaign').value = ORIGINAL_SET; }
        try {
            const boards = resolveSet(library, library.campaignId, Originals);
            $('campaign-status').textContent = `${boards.length} level${boards.length === 1 ? '' : 's'} in order · 5 lives · score carries forward · repeats with increasing difficulty`;
            $('campaign-status').className = 'muted';
            $('start-game').disabled = !Game.ready;
            const ids = library.campaignId === ORIGINAL_SET ? Originals.map((_, index) => originalId(index)) : library.sets.find(set => set.id === library.campaignId).boardIds;
            $('campaign-route').textContent = boards.map((board, index) => `${index + 1}. ${board.name} [${isOriginal(ids[index]) ? 'Original' : 'Custom'}]`).join(' → ');
        } catch (error) {
            $('campaign-status').textContent = error.message + ' Open Library to edit this set.';
            $('campaign-status').className = 'invalid-message';
            $('start-game').disabled = true;
            $('campaign-route').textContent = '';
        }
    },

    playSelected() {
        const library = this.workshop.library;
        this.workshop.attempt(() => {
            const boards = resolveSet(library, library.campaignId, Originals);
            const name = library.campaignId === ORIGINAL_SET ? 'Original campaign' : library.sets.find(set => set.id === library.campaignId).name;
            this.workshop.play(boards, name, { originalCampaign: library.campaignId === ORIGINAL_SET });
        });
    },

    renderCards() {
        const query = $('board-search').value.trim().toLowerCase();
        const filter = $('board-filter').value;
        const boards = this.allBoards().filter(board => {
            const original = isOriginal(board.id);
            return board.name.toLowerCase().includes(query) &&
                (filter !== 'custom' || !original) && (filter !== 'original' || original) && (filter !== 'draft' || !this.playable(board));
        });
        $('board-cards').replaceChildren();
        $('library-count').textContent = `${boards.length} shown · ${this.workshop.boards.length} custom boards`;
        for (const original of [true, false]) {
            const group = boards.filter(board => isOriginal(board.id) === original);
            if (!group.length) continue;
            const heading = document.createElement('h3'); heading.className = 'board-group-title';
            heading.textContent = `${original ? 'Original collection' : 'Custom boards'} · ${group.length}`;
            $('board-cards').append(heading);
        for (const board of group) {
            const card = button('', () => { this.previewId = board.id; this.render(); }, `Preview ${board.name || 'Untitled'}`);
            card.className = `board-card${original ? ' original-card' : ''}`;
            card.setAttribute('aria-pressed', String(board.id === this.previewId));
            const thumbnail = document.createElement('canvas');
            thumbnail.width = 316; thumbnail.height = 100; thumbnail.setAttribute('aria-hidden', 'true');
            const ctx = thumbnail.getContext('2d');
            ctx.fillStyle = '#0d150f'; ctx.fillRect(0, 0, 316, 100); ctx.fillStyle = '#afd482'; ctx.font = '5px monospace';
            board.layout.slice(0, 20).forEach((row, y) => [...row.slice(0, 79)].forEach((char, x) => ctx.fillText(char, x * 4, y * 5 + 5)));
            const title = document.createElement('strong'); title.textContent = board.name || 'Untitled';
            const status = document.createElement('span');
            status.textContent = original ? 'Fixed original · no revisions' : this.playable(board) ? 'Format valid' : 'Draft — needs fixes';
            card.append(originBadge(original), thumbnail, title, status); $('board-cards').append(card);
        }
        }
        $('library-empty').hidden = boards.length > 0;
    },

    renderPreview() {
        const board = this.preview();
        $('preview-title').textContent = board.name || 'Untitled board';
        $('preview-map').textContent = board.layout.slice(0, 20).map(row => row.slice(0, 79).padEnd(79, ' ')).join('\n');
        const uses = this.workshop.library.sets.filter(set => set.boardIds.includes(this.previewId));
        const generators = board.layout.join('').split('V').length - 1;
        $('preview-info').textContent = `${generators} generator${generators === 1 ? '' : 's'} · Used in ${uses.length} custom set${uses.length === 1 ? '' : 's'}${uses.length ? ': ' + uses.map(set => set.name).join(', ') : ''}`;
        $('preview-test').disabled = !Game.ready || !this.playable(board);
        const original = isOriginal(this.previewId);
        $('preview-origin').replaceChildren(originBadge(original));
        $('preview-test').textContent = original ? 'Play original board →' : 'Test custom draft →';
        $('preview-protection').textContent = original ? 'This is one of the seven original boards. It cannot be edited, revised, or deleted by anyone, including admins. Make a custom copy to create a variant.' : 'Custom board. Drafts and saved revisions belong to your library. A copy or import is always custom, even when it has an original board’s name or layout.';
        $('preview-info').textContent = `${original ? 'Original board' : 'Current draft'} · ${$('preview-info').textContent}`;
        $('preview-edit').textContent = original ? 'Make custom copy' : 'Edit custom board';
        $('preview-edit').disabled = original && this.workshop.boards.length >= MAX_BOARDS;
        $('preview-compare').disabled = original;
        $('preview-compare').hidden = original;
        $('preview-add').textContent = original ? 'Add to selected Series' : 'Save & add to Series';
        $('preview-add').disabled = this.currentSet().id === ORIGINAL_SET || this.currentSet().boardIds.length >= MAX_BOARDS;
        try { normalizeBoard(board); $('preview-validation').textContent = 'Ready to test. A test starts a separate game and does not change any set.'; }
        catch (error) { $('preview-validation').textContent = error.message; }
    },

    renderSets() {
        const workshop = this.workshop;
        const set = this.currentSet();
        const original = set.id === ORIGINAL_SET;
        $('set-select').replaceChildren(new Option('Original campaign - 7 boards', ORIGINAL_SET), ...workshop.library.sets.map(item => new Option(`Custom Series · ${item.name}`, item.id)));
        $('set-select').value = set.id;
        $('set-name').value = set.name; $('set-name').disabled = original;
        $('delete-set').disabled = $('export-set').disabled = original;
        $('new-set').disabled = workshop.library.sets.length >= MAX_SETS;
        $('set-route').replaceChildren();
        set.boardIds.forEach((id, index) => {
            const row = document.createElement('li');
            const board = resolveEntry(workshop.library, set, index, Originals);
            const select = button(`${index + 1}. ${board?.name || 'Missing board'}${board?.number ? ' · r' + board.number : ''}`, () => { this.previewId = id; this.render(); });
            select.className = 'route-name'; row.append(select);
            row.append(originBadge(isOriginal(id)));
            if (!original) {
                const source = workshop.boards.find(item => item.id === id);
                if (source) {
                    const latest = source.revisions?.at(-1);
                    const newer = latest && latest.number > board.number;
                    row.append(button(newer ? `New r${latest.number} · Compare` : 'Compare', () => RevisionUI.open(id, set.id, index), `Compare revisions for level ${index + 1}${newer ? ' — newer revision available' : ''}`));
                }
                for (const [offset, text] of [[-1, '↑'], [1, '↓']]) {
                    const move = button(text, () => {
                        workshop.remember();
                        const [boardId] = set.boardIds.splice(index, 1); set.boardIds.splice(index + offset, 0, boardId);
                        const [revisionId] = set.revisionIds.splice(index, 1); set.revisionIds.splice(index + offset, 0, revisionId);
                        workshop.changed();
                    }, `Move level ${index + 1} ${offset < 0 ? 'up' : 'down'}`);
                    move.disabled = index + offset < 0 || index + offset >= set.boardIds.length; row.append(move);
                }
                row.append(button('×', () => { workshop.remember(); set.boardIds.splice(index, 1); set.revisionIds.splice(index, 1); workshop.changed(); }, `Remove level ${index + 1} from set`));
            }
            $('set-route').append(row);
        });
        $('set-help').textContent = original ? 'Seven original boards, in their original order.' : `${set.boardIds.length} / ${MAX_BOARDS} levels · Entries keep their saved revisions until you adopt an update.`;
        $('set-empty').hidden = set.boardIds.length > 0;
        try { resolveSet(workshop.library, set.id, Originals); $('set-validation').textContent = 'Ready to play.'; $('choose-set').disabled = false; }
        catch (error) { $('set-validation').textContent = error.message; $('choose-set').disabled = true; }
    }
};
